import { useMutation } from '@apollo/client/react';
import { UPDATE_CUSTOMER_FROZEN_MUTATION } from '../../integrations/allkort/graphql/gql/CustomerMutations';
import {
	UpdateCustomerFrozenInput,
	UpdateCustomerFrozenResponse,
} from '../../integrations/allkort/graphql/interface/CustomerMutation';

/**
 * useUpdateCustomerFrozen
 *
 * Custom hook for freezing or unfreezing a customer account.
 *
 * @example
 * ```tsx
 * const { freezeCustomer, loading, error } = useUpdateCustomerFrozen();
 *
 * await freezeCustomer({ id: customerId, reason: 'Frozen by delete account' });
 * ```
 */
export const useUpdateCustomerFrozen = () => {
	const [updateCustomerFrozenMutation, { loading, error, data }] = useMutation<
		UpdateCustomerFrozenResponse,
		UpdateCustomerFrozenInput
	>(UPDATE_CUSTOMER_FROZEN_MUTATION, { errorPolicy: 'all' });

	const freezeCustomer = async (input: Pick<UpdateCustomerFrozenInput, 'id' | 'reason'>) => {
		return updateCustomerFrozenMutation({
			variables: {
				id: input.id,
				is: true,
				reason: input.reason,
			},
		});
	};

	const unfreezeCustomer = async (input: Pick<UpdateCustomerFrozenInput, 'id' | 'reason'>) => {
		return updateCustomerFrozenMutation({
			variables: {
				id: input.id,
				is: false,
				reason: input.reason,
			},
		});
	};

	return { freezeCustomer, unfreezeCustomer, loading, error, data };
};
